/**
 * THE LOADING WIPE — a port of sekei.xyz/p/loading-wipe.
 *
 * A coloured ribbon sweeps across the card at an angle and, where it has passed, the canvas writes zero
 * alpha so the real DOM underneath shows through. That last part is the whole trick: this is not an overlay
 * that fades out, it is a plane being carried off an edge, and the page is revealed by its absence.
 *
 * WHY THIS IS A SHADER AND NOT CSS. Three things in the reference have no CSS equivalent, and two earlier
 * CSS attempts died on exactly them:
 *
 *   1. the sweep edge is `dot(uv, dir) + bias` — a signed distance from a line — BENT per-pixel by FBM. A
 *      mask gradient gives you the line; an SVG displacement filter warps the whole layer including its
 *      colour, not the distance field, so the edge wobbles but the ribbon smears with it;
 *   2. the ribbon's four-stop ramp is sampled by that same distance, so the colours travel WITH the edge
 *      and are swirled by a second, independent noise field. In CSS the ramp is fixed to the box;
 *   3. the grain is applied to the DISTANCE, not to the finished alpha — see fs_main. Offsetting alpha
 *      instead punches holes through the untouched surface.
 *
 * WHAT IS OURS, NOT THE REFERENCE'S. The reference rasterises its whole loading page into `pageTexture` so
 * the skeleton is carried off with the ribbon. A web card's loading surface is a flat white plane, and a
 * 1x1 texture of that colour is an EXACT raster of it — so the sampling path is unchanged and nothing is
 * approximated. Give this a real bitmap the day the loading card grows a skeleton.
 *
 * WebGPU only. `start` reports failure rather than throwing, and the caller reveals instantly instead.
 */

/** The reference's own defaults, verbatim. */
export const WIPE_DEFAULTS = {
  angleDeg: 270,
  durationMs: 1200,
  band: 0.65,
  bleed: 0.73,
  feather: 0.17,
  turbulence: 0.16,
  noiseScale: 3,
  swirl: 0.22,
  grainSize: 1.5,
  grainAmount: 0.12,
  /** The plane being carried off. Flat, so a 1x1 texture of it is exact. */
  surface: '#ffffff',
  /** Ribbon stops, revealed edge → surface. */
  colors: ['#6366f1', '#38bdf8', '#67e8f9', '#e0f2fe'],
} as const

export type WipeOptions = {
  -readonly [K in keyof typeof WIPE_DEFAULTS]: K extends 'colors' ? string[] : (typeof WIPE_DEFAULTS)[K]
}

const SHADER = /* wgsl */ `
struct Params {
  // Sweep axis: dot(uv, dir) + bias runs 0 -> 1 across the square.
  dir: vec2f,
  bias: f32,
  progress: f32,
  band: f32,
  bleed: f32,
  feather: f32,
  turbulence: f32,
  noiseScale: f32,
  swirl: f32,
  grainSize: f32,
  grainAmount: f32,
  // Device pixels, so grain is the same gauge at any size.
  resolution: vec2f,
  _pad: vec2f,
  // Ribbon stops, revealed edge -> surface. w unused.
  stops: array<vec4f, 4>,
}

@group(0) @binding(0) var<uniform> params: Params;
@group(0) @binding(1) var pageSampler: sampler;
@group(0) @binding(2) var pageTexture: texture_2d<f32>;

struct VSOut {
  @builtin(position) position: vec4f,
  @location(0) uv: vec2f,
}

// One oversized triangle rather than two: no seam down the diagonal.
@vertex fn vs_main(@builtin(vertex_index) index: u32) -> VSOut {
  var corners = array(vec2f(-1.0, -1.0), vec2f(3.0, -1.0), vec2f(-1.0, 3.0));
  let p = corners[index];
  var out: VSOut;
  out.position = vec4f(p, 0.0, 1.0);
  // Top-origin uv, to match how the sweep angles are described.
  out.uv = vec2f((p.x + 1.0) * 0.5, (1.0 - p.y) * 0.5);
  return out;
}

fn hash21(p: vec2f) -> f32 {
  var q = fract(p * vec2f(123.34, 345.45));
  q += dot(q, q + 34.345);
  return fract(q.x * q.y);
}

fn valueNoise(p: vec2f) -> f32 {
  let i = floor(p);
  let f = fract(p);
  let u = f * f * (3.0 - 2.0 * f);
  let a = hash21(i);
  let b = hash21(i + vec2f(1.0, 0.0));
  let c = hash21(i + vec2f(0.0, 1.0));
  let d = hash21(i + vec2f(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// Three octaves, normalised to roughly [0, 1].
fn fbm(p: vec2f) -> f32 {
  var value = 0.0;
  var amplitude = 0.5;
  var q = p;
  for (var i = 0; i < 3; i++) {
    value += amplitude * valueNoise(q);
    q = q * 2.13 + vec2f(17.3, 9.1);
    amplitude *= 0.5;
  }
  return value / 0.875;
}

// 32-bit bit-mixing hash (lowbias32). hash21 above is a fract-multiply hash: fine for fbm, whose inputs are
// small, but fed the large integer coordinates a per-pixel grain needs, its fractional parts cycle with a
// short period and f32 precision collapses — visible as diagonal lattices across the grain.
fn hashU32(value: u32) -> u32 {
  var h = value;
  h ^= h >> 16u;
  h *= 0x7feb352du;
  h ^= h >> 15u;
  h *= 0x846ca68bu;
  h ^= h >> 16u;
  return h;
}

fn grainAt(uv: vec2f) -> f32 {
  let cell = vec2u(max(
    floor(uv * params.resolution / max(params.grainSize, 1.0)),
    vec2f(0.0)
  ));
  let mixed = hashU32(cell.x ^ hashU32(cell.y * 0x9e3779b9u));
  return f32(mixed) / 4294967295.0 - 0.5;
}

// Signed distance from the (turbulent) edge, in sweep units: negative is swept away, [0, band] is inside
// the ribbon, beyond that is untouched surface.
fn wipeDistance(uv: vec2f, progress: f32) -> f32 {
  let d = dot(uv, params.dir) + params.bias;
  let bend = fbm(uv * params.noiseScale + vec2f(progress * 0.6, -progress * 0.4)) * 2.0 - 1.0;
  let edge = mix(
    -(params.band + params.turbulence),
    1.0 + params.turbulence + params.feather,
    progress
  );
  return d - edge + bend * params.turbulence;
}

// Four-stop ramp across the ribbon, smoothstepped between stops.
fn rampColor(t: f32) -> vec3f {
  let x = clamp(t, 0.0, 1.0) * 3.0;
  let i = min(u32(x), 2u);
  let f = smoothstep(0.0, 1.0, x - f32(i));
  return mix(params.stops[i].rgb, params.stops[i + 1u].rgb, f);
}

@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let grain = grainAt(uv) * params.grainAmount;
  let s = wipeDistance(uv, params.progress);
  // Grain the edge distance, not the finished alpha. Offsetting alpha would punch holes through the
  // untouched page too; offsetting the distance only breaks up the edge as it travels.
  let alpha = smoothstep(0.0, params.feather, s + grain * params.feather * 2.0);
  // Swirl the ramp coordinate with an independent noise field, so the colours smear into each other
  // instead of striping.
  let swirl = (fbm(uv * 2.0 + vec2f(4.7, 8.1)) - 0.5) * params.swirl;
  let bandT = clamp(s / params.band + swirl, 0.0, 1.0);
  let bandWeight = 1.0 - smoothstep(params.band * params.bleed, params.band, s);
  let page = textureSample(pageTexture, pageSampler, uv).rgb;
  let ribbon = clamp(rampColor(bandT) * (1.0 + grain * 0.5), vec3f(0.0), vec3f(1.0));
  let rgb = mix(page, ribbon, bandWeight);
  // Premultiplied: the canvas is transparent where the sweep has passed.
  return vec4f(rgb * alpha, alpha);
}
`

/** Angle → a unit-ish axis plus the bias that makes `dot(uv, dir) + bias` run 0→1 across the square. */
function axisFor(angleDeg: number): { dir: [number, number]; bias: number } {
  const rad = (angleDeg * Math.PI) / 180
  const cx = Math.cos(rad)
  const cy = Math.sin(rad)
  const span = Math.abs(cx) + Math.abs(cy)
  const low = Math.min(0, cx) + Math.min(0, cy)
  return { dir: [cx / span, cy / span], bias: -low / span }
}

const HEX = /^#[0-9a-f]{6}$/i
function rgbOf(hex: string): [number, number, number] {
  if (!HEX.test(hex)) return [0, 0, 0]
  const n = Number.parseInt(hex.slice(1), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

// The WebGPU globals are not in this project's TS lib, and pulling @webgpu/types in for one file would put a
// whole API surface into every build. The handful of calls used here are typed structurally instead.
type Gpu = {
  requestAdapter(): Promise<{ requestDevice(): Promise<GpuDevice> } | null>
  getPreferredCanvasFormat(): string
}
interface GpuDevice {
  createShaderModule(d: { code: string }): unknown
  createRenderPipeline(d: Record<string, unknown>): { getBindGroupLayout(i: number): unknown }
  createBuffer(d: { size: number; usage: number }): { destroy(): void }
  createSampler(d: Record<string, unknown>): unknown
  createTexture(d: Record<string, unknown>): { createView(): unknown; destroy(): void }
  createBindGroup(d: Record<string, unknown>): unknown
  createCommandEncoder(): {
    beginRenderPass(d: Record<string, unknown>): {
      setPipeline(p: unknown): void
      setBindGroup(i: number, g: unknown): void
      draw(n: number): void
      end(): void
    }
    finish(): unknown
  }
  queue: {
    writeBuffer(b: unknown, off: number, data: ArrayBufferView): void
    copyExternalImageToTexture(src: Record<string, unknown>, dst: Record<string, unknown>, size: number[]): void
    submit(c: unknown[]): void
  }
  destroy(): void
}

/**
 * Play the wipe over `canvas` once. Returns a cancel.
 *
 * `onFinish` fires when the sweep has fully passed OR when WebGPU is unavailable — either way the page is
 * revealed, so the caller can drop the canvas on the same signal and never has to branch on support.
 */
export function runWipe(
  canvas: HTMLCanvasElement,
  options: Partial<WipeOptions>,
  onFinish: () => void,
): () => void {
  const opts: WipeOptions = { ...WIPE_DEFAULTS, colors: [...WIPE_DEFAULTS.colors], ...options }
  const gpu = (navigator as unknown as { gpu?: Gpu }).gpu
  let cancelled = false
  let raf = 0
  let teardown: (() => void) | null = null

  if (!gpu) {
    onFinish()
    return () => {}
  }

  void (async () => {
    let device: GpuDevice | null = null
    try {
      const adapter = await gpu.requestAdapter()
      if (!adapter) throw new Error('no adapter')
      device = await adapter.requestDevice()
      const context = canvas.getContext('webgpu') as {
        configure(d: Record<string, unknown>): void
        unconfigure(): void
        getCurrentTexture(): { createView(): unknown }
      } | null
      if (!context) throw new Error('no context')

      const format = gpu.getPreferredCanvasFormat()
      // Premultiplied is what lets zero alpha read as "the DOM below shows through".
      context.configure({ device, format, alphaMode: 'premultiplied' })

      const module = device.createShaderModule({ code: SHADER })
      const pipeline = device.createRenderPipeline({
        layout: 'auto',
        vertex: { module, entryPoint: 'vs_main' },
        fragment: { module, entryPoint: 'fs_main', targets: [{ format }] },
        primitive: { topology: 'triangle-list' },
      })
      const uniform = device.createBuffer({ size: 128, usage: 0x40 | 0x8 }) // UNIFORM | COPY_DST
      const sampler = device.createSampler({
        magFilter: 'nearest',
        minFilter: 'nearest',
        addressModeU: 'clamp-to-edge',
        addressModeV: 'clamp-to-edge',
      })

      // The surface being carried off, as a 1x1 bitmap — exact for a flat plane.
      const [sr, sg, sb] = rgbOf(opts.surface)
      const surfacePx = new Uint8ClampedArray([sr * 255, sg * 255, sb * 255, 255])
      const bitmap = await createImageBitmap(new ImageData(surfacePx, 1, 1))
      const texture = device.createTexture({
        size: [1, 1],
        format: 'rgba8unorm',
        usage: 0x4 | 0x2 | 0x10, // TEXTURE_BINDING | COPY_DST | RENDER_ATTACHMENT
      })
      device.queue.copyExternalImageToTexture({ source: bitmap }, { texture }, [1, 1])
      const bindGroup = device.createBindGroup({
        layout: pipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: uniform } },
          { binding: 1, resource: sampler },
          { binding: 2, resource: texture.createView() },
        ],
      })

      const scratch = new Float32Array(32)
      const { dir, bias } = axisFor(opts.angleDeg)
      const dev = device

      teardown = () => {
        texture.destroy()
        uniform.destroy()
        try {
          context.unconfigure()
        } catch {
          /* already gone */
        }
        dev.destroy()
      }
      if (cancelled) {
        teardown()
        return
      }

      const draw = (progress: number) => {
        const dpr = Math.min(globalThis.devicePixelRatio || 1, 2)
        const w = Math.max(Math.round(canvas.clientWidth * dpr), 1)
        const h = Math.max(Math.round(canvas.clientHeight * dpr), 1)
        if (canvas.width !== w) canvas.width = w
        if (canvas.height !== h) canvas.height = h

        scratch[0] = dir[0]
        scratch[1] = dir[1]
        scratch[2] = bias
        scratch[3] = progress
        scratch[4] = opts.band
        scratch[5] = opts.bleed
        scratch[6] = opts.feather
        scratch[7] = opts.turbulence
        scratch[8] = opts.noiseScale
        scratch[9] = opts.swirl
        scratch[10] = opts.grainSize
        scratch[11] = opts.grainAmount
        scratch[12] = w
        scratch[13] = h
        for (let i = 0; i < 4; i++) {
          const [r, g, b] = rgbOf(opts.colors[i] ?? '#ffffff')
          scratch[16 + i * 4] = r
          scratch[17 + i * 4] = g
          scratch[18 + i * 4] = b
          scratch[19 + i * 4] = 1
        }
        dev.queue.writeBuffer(uniform, 0, scratch)

        const encoder = dev.createCommandEncoder()
        const pass = encoder.beginRenderPass({
          colorAttachments: [
            {
              view: context.getCurrentTexture().createView(),
              clearValue: { r: 0, g: 0, b: 0, a: 0 },
              loadOp: 'clear',
              storeOp: 'store',
            },
          ],
        })
        pass.setPipeline(pipeline)
        pass.setBindGroup(0, bindGroup)
        pass.draw(3)
        pass.end()
        dev.queue.submit([encoder.finish()])
      }

      const t0 = performance.now()
      const tick = (now: number) => {
        if (cancelled) return
        const p = Math.min(1, (now - t0) / opts.durationMs)
        draw(p)
        if (p < 1) raf = requestAnimationFrame(tick)
        else onFinish()
      }
      draw(0)
      raf = requestAnimationFrame(tick)
    } catch {
      device?.destroy()
      if (!cancelled) onFinish()
    }
  })()

  return () => {
    cancelled = true
    cancelAnimationFrame(raf)
    teardown?.()
  }
}
